'use client';

import React, { useState } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import '../../../lib/firebase-client';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { checkBillingUnpaid } from '../../../services/website';
import Link from 'next/link';
import GlassCard from '@/components/onboarding/GlassCard';

const schema = yup
  .object({
    email: yup.string().email('Please enter a valid email address').required('Email is required'),
    password: yup.string().required('Password is required'),
  })
  .required();

const Login = () => {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (formData) => {
    setSubmitting(true);
    setError(null);

    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);

      const idToken = await userCredential.user.getIdToken();
      await fetch('/api/login', {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      const uid = userCredential.user.uid;
      const db = getFirestore();
      const membership = await getDocs(
        query(
          collection(db, 'memberships'),
          where('uid', '==', uid),
          orderBy('created_at')
        )
      );

      if (membership.docs.length === 0) {
        setError('No account found. Please register first.');
        setSubmitting(false);
        return;
      }

      const orgId = membership.docs[0].data().orgId;

      const billingUnpaid = await checkBillingUnpaid(orgId);
      if (billingUnpaid) {
        window.location.href = `/org/${orgId}/settings/plan-billing`;
        return;
      }
      window.location.href = `/org/${orgId}/websites`;
    } catch (err) {
      console.error('[Login] Error:', err);
      setError('Email or password is incorrect. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-6">
        {/* Logo + heading */}
        <div className="text-center">
          <img src="/images/logos/tivion-tivit-logo-cyan.png" alt="Tivion" className="mx-auto h-10 w-10" />
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground">
            Sign in to Tivion
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Or{' '}
            <Link href="/onboarding/identity" className="font-medium text-primary hover:text-primary/80">
              start your 30-day free trial
            </Link>
          </p>
        </div>

        <GlassCard>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground">
                Email address
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full rounded-lg border border-border bg-card/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/40"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </label>
                <Link href="/forgot-password" className="text-xs font-medium text-primary hover:text-primary/80">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                className="w-full rounded-lg border border-border bg-card/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/40"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </GlassCard>
      </div>
    </div>
  );
};

export default Login;
